import type { Reader, Writer } from "../stream.ts";
import { type IetfVersion, Version } from "./version.ts";

const GROUP_END = 0x03;

// draft-18 adds bit 0x40 (FIRST_OBJECT) to the subgroup header type per spec
// 11.4.2. moq-lite always starts subgroups at object 0, so the bit carries no
// extra information for us: set it on emit, strip it on parse.
const FIRST_OBJECT_BIT = 0x40;

function hasFirstObjectBit(version: IetfVersion): boolean {
	switch (version) {
		case Version.DRAFT_14:
		case Version.DRAFT_15:
		case Version.DRAFT_16:
		case Version.DRAFT_17:
			return false;
		default:
			return true;
	}
}

export interface GroupFlags {
	hasExtensions: boolean;
	hasSubgroup: boolean;
	hasSubgroupObject: boolean;
	hasEnd: boolean;
	// v15: whether priority is present in the header.
	// When false (0x30 base), priority inherits from the control message.
	hasPriority: boolean;
}

/**
 * STREAM_HEADER_SUBGROUP from moq-transport spec.
 * Used for stream-per-group delivery mode.
 */
export class Group {
	flags: GroupFlags;
	trackAlias: bigint;
	groupId: number;
	subGroupId: number;
	publisherPriority: number;

	constructor({
		trackAlias,
		groupId,
		subGroupId,
		publisherPriority,
		flags,
	}: {
		trackAlias: bigint;
		groupId: number;
		subGroupId: number;
		publisherPriority: number;
		flags: GroupFlags;
	}) {
		this.flags = flags;
		this.trackAlias = trackAlias;
		this.groupId = groupId;
		this.subGroupId = subGroupId;
		this.publisherPriority = publisherPriority;
	}

	async encode(w: Writer, version: IetfVersion): Promise<void> {
		if (!this.flags.hasSubgroup && this.subGroupId !== 0) {
			throw new Error(`Subgroup ID must be 0 if hasSubgroup is false: ${this.subGroupId}`);
		}

		const base = this.flags.hasPriority ? 0x10 : 0x30;
		let id = base;
		if (this.flags.hasExtensions) {
			id |= 0x01;
		}
		if (this.flags.hasSubgroupObject) {
			id |= 0x02;
		}
		if (this.flags.hasSubgroup) {
			id |= 0x04;
		}
		if (this.flags.hasEnd) {
			id |= 0x08;
		}
		if (hasFirstObjectBit(version)) {
			id |= FIRST_OBJECT_BIT;
		}
		await w.u53(id);
		await w.u62(this.trackAlias);
		await w.u53(this.groupId);
		if (this.flags.hasSubgroup) {
			await w.u53(this.subGroupId);
		}
		if (this.flags.hasPriority) {
			await w.u8(this.publisherPriority);
		}
	}

	static async decode(r: Reader, version: IetfVersion): Promise<Group> {
		const raw = await r.u53();
		// Strip the draft-18 FIRST_OBJECT bit before the range check.
		const id = hasFirstObjectBit(version) ? raw & ~FIRST_OBJECT_BIT : raw;

		let hasPriority: boolean;
		let baseId: number;
		if (id >= 0x10 && id <= 0x1f) {
			hasPriority = true;
			baseId = id;
		} else if (id >= 0x30 && id <= 0x3f) {
			hasPriority = false;
			baseId = id - (0x30 - 0x10);
		} else {
			throw new Error(`Unsupported group type: ${id}`);
		}

		const flags: GroupFlags = {
			hasExtensions: (baseId & 0x01) !== 0,
			hasSubgroupObject: (baseId & 0x02) !== 0,
			hasSubgroup: (baseId & 0x04) !== 0,
			hasEnd: (baseId & 0x08) !== 0,
			hasPriority,
		};

		const trackAlias = await r.u62();
		const groupId = await r.u53();
		const subGroupId = flags.hasSubgroup ? await r.u53() : 0;
		const publisherPriority = hasPriority ? await r.u8() : 128; // Default priority when absent

		return new Group({ trackAlias, groupId, subGroupId, publisherPriority, flags });
	}
}

export class Frame {
	// undefined means end of group
	payload?: Uint8Array;

	constructor({ payload }: { payload?: Uint8Array } = {}) {
		this.payload = payload;
	}

	async encode(w: Writer, flags: GroupFlags): Promise<void> {
		await w.u53(0); // id_delta = 0

		if (flags.hasExtensions) {
			await w.u53(0); // extensions length = 0
		}

		if (this.payload !== undefined) {
			await w.u53(this.payload.byteLength);

			if (this.payload.byteLength === 0) {
				await w.u53(0); // status = normal
			} else {
				await w.write(this.payload);
			}
		} else {
			await w.u53(0); // length = 0
			await w.u53(GROUP_END);
		}
	}

	static async decode(r: Reader, flags: GroupFlags): Promise<Frame> {
		const delta = await r.u53();
		if (delta !== 0) {
			throw new Error(`object ID delta is not supported: ${delta}`);
		}

		if (flags.hasExtensions) {
			const extensionsLength = await r.u53();
			// We don't care about extensions
			await r.read(extensionsLength);
		}

		const payloadLength = await r.u53();

		if (payloadLength > 0) {
			const payload = await r.read(payloadLength);
			return new Frame({ payload });
		}

		const status = await r.u53();

		if (flags.hasEnd) {
			// Empty frame
			if (status === 0) return new Frame({ payload: new Uint8Array(0) });
		} else if (status === 0 || status === GROUP_END) {
			// TODO status === 0 should be an empty frame, but moq-rs seems to be sending it incorrectly on group end.
			return new Frame();
		}

		throw new Error(`Unsupported object status: ${status}`);
	}
}
